/*
 * Copyright 2021 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {AriaColorAreaProps, useColorArea} from 'react-aria/useColorArea';

import {classNames} from '../utils/classNames';
import {ColorAreaContext} from 'react-aria-components/ColorArea';
import {ColorThumb} from './ColorThumb';
import {dimensionValue, useStyleProps} from '../utils/styleProps';
import {DimensionValue, FocusableRef, StyleProps} from '@react-types/shared';
import {mergeProps} from 'react-aria/mergeProps';
import React, {ReactElement, useRef} from 'react';
import styles from '@adobe/spectrum-css-temp/components/colorarea/vars.css';
import {useColorAreaState} from 'react-stately/useColorAreaState';
import {useContextProps} from 'react-aria-components/slots';
import {useFocusableRef} from '../utils/useDOMRef';
import {useFocusRing} from 'react-aria/useFocusRing';
import {useProviderProps} from '../provider/Provider';

export interface SpectrumColorAreaProps
  extends AriaColorAreaProps, Omit<StyleProps, 'width' | 'height'> {
  /** Size of the Color Area. */
  size?: DimensionValue;
}

/**
 * ColorArea allows users to adjust two channels of an RGB, HSL or HSB color value against a
 * two-dimensional gradient background.
 */
export const ColorArea = React.forwardRef(function ColorArea(
  props: SpectrumColorAreaProps,
  ref: FocusableRef<HTMLDivElement>
) {
  props = useProviderProps(props);
  let inputXRef = useRef(null);
  let inputYRef = useRef(null);
  let containerRef = useFocusableRef(ref, inputXRef);
  [props, containerRef] = useContextProps(props, containerRef, ColorAreaContext);

  let {isDisabled} = props;
  let size = props.size && dimensionValue(props.size);
  let {styleProps} = useStyleProps(props);

  let state = useColorAreaState(props);

  let {colorAreaProps, xInputProps, yInputProps, thumbProps} = useColorArea(
    {...props, inputXRef, inputYRef, containerRef},
    state
  );
  let {focusProps, isFocusVisible} = useFocusRing();

  return (
    <div
      {...colorAreaProps}
      className={classNames(
        styles,
        'spectrum-ColorArea',
        {
          'is-disabled': isDisabled
        },
        styleProps.className
      )}
      ref={containerRef}
      style={{
        ...(isDisabled ? {} : colorAreaProps.style),
        ...styleProps.style,
        // Workaround around https://github.com/adobe/spectrum-css/issues/1032
        width: size,
        height: size
      }}>
      <ColorThumb
        value={state.getDisplayColor()}
        isFocused={isFocusVisible}
        isDisabled={isDisabled}
        isDragging={state.isDragging}
        containerRef={containerRef}
        className={classNames(styles, 'spectrum-ColorArea-handle')}
        {...thumbProps}>
        <div role="presentation">
          <input
            className={classNames(styles, 'spectrum-ColorArea-slider')}
            {...mergeProps(xInputProps, focusProps)}
            ref={inputXRef}
          />
          <input
            className={classNames(styles, 'spectrum-ColorArea-slider')}
            {...mergeProps(yInputProps, focusProps)}
            ref={inputYRef}
          />
        </div>
      </ColorThumb>
    </div>
  );
}) as (props: SpectrumColorAreaProps & {ref?: FocusableRef<HTMLDivElement>}) => ReactElement;
